
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, Calendar, Award, ArrowRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface Organizer {
  id: string;
  name: string;
  rating: number;
  eventsPerYear: number;
  avatar?: string;
  upcomingEvents: number;
  sports: string[];
}

interface FeaturedOrganizersSectionProps {
  organizers: Organizer[];
  onOrganizerClick: (organizer: Organizer) => void;
  selectedSport: string;
  onExploreMore?: () => void;
}

const FeaturedOrganizersSection = ({ organizers, onOrganizerClick, selectedSport, onExploreMore }: FeaturedOrganizersSectionProps) => {
  const formatEventsCount = (count: number) => {
    return count > 100 ? "100+ eventos realizados" : `${count} eventos realizados`;
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  // Show all organizers passed as props (no sport filter)
  const filteredOrganizers = organizers;

  return (
    <Card className="bg-white pb-6 px-0 md:px-6 -mx-2 md:mx-0">
      <CardHeader className="py-6 px-4 md:px-6">
        <div className="flex items-center justify-between gap-4 md:gap-8">
          <CardTitle className="text-sm md:text-xl font-bold text-gray-900 flex items-center">
            <Award className="h-5 w-5 mr-2 text-yellow-600" />
            Organizadores destacados
          </CardTitle>
          {onExploreMore && (
            <Button variant="outline" size="sm" onClick={onExploreMore} className="flex items-center gap-1 text-xs md:text-sm h-6 md:h-10 px-2 md:px-4">
              Ver todos
              <ArrowRight className="h-3 w-3 md:h-4 md:w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 md:px-6">
        <div className="space-y-4">
          {filteredOrganizers.map((organizer) => (
            <Card 
              key={organizer.id} 
              className="hover:shadow-md transition-shadow cursor-pointer border-gray-200"
              onClick={() => onOrganizerClick(organizer)}
            >
              <CardContent className="p-0.5 md:p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={organizer.avatar} alt={organizer.name} />
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {getInitials(organizer.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="text-xs md:text-base font-semibold text-gray-900">{organizer.name}</h3>
                      <div className="flex items-center text-[10px] md:text-sm text-gray-600 mt-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{organizer.upcomingEvents} próximos eventos</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center justify-end mb-1">
                      <Star className="h-4 w-4 text-yellow-500 mr-1" />
                      <span className="font-semibold text-gray-900">{Number(organizer.rating).toFixed(1)}</span>
                    </div>
                    <p className="text-[10px] md:text-xs text-gray-500">
                      {formatEventsCount(organizer.eventsPerYear)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default FeaturedOrganizersSection;
